import { useIntl } from 'react-intl';
import { useLoaderData, type LoaderFunctionArgs } from 'react-router-dom';
import type { GetApiCallResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { resolvedPath } from '@/lib/api-calls/resolved-path';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ClockIcon } from '@/components/layout/icons';
import { ResourceHeader } from '@/components/resource-header';

type ApiCallDetailData = {
  readonly call: GetApiCallResponse;
};

export async function apiCallDetailLoader({ params }: LoaderFunctionArgs): Promise<ApiCallDetailData> {
  const callId = params['callId'];

  if (typeof callId !== 'string' || callId.length === 0) {
    throw new Response('Missing API call id', { status: 404 });
  }

  // No key scoping in the path — a keyless or workspace-less entry loads exactly
  // like any other (the IMM-441 / IMM-446 fix). A 404 surfaces the not-found
  // state through the route error boundary rather than an error page.
  const result = await apiClient.adminApiCalls.get({ pathParams: { callId } });

  if (!result.ok) {
    throw new Response('Failed to load API call', { status: result.status });
  }

  return { call: result.body };
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

// Bodies are stored as text (JSON payloads). Pretty-print when parseable, else
// show the raw stored text verbatim — never re-process (shown as stored).
function prettyBody(body: string | null): string | null {
  if (body === null) return null;

  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

function labelFor(name: string | null, id: string | null, fallback: string): string {
  if (name !== null && name !== '') return name;

  if (id !== null && id !== '') return id;

  return fallback;
}

export function ApiCallDetailPage() {
  const t = useTranslations('pages.admin.api-calls');
  const tDetail = useTranslations('pages.admin.api-calls.detail');
  const intl = useIntl();
  const { call } = useLoaderData() as ApiCallDetailData;

  const placeholder = t('placeholder');
  const requestBody = prettyBody(call.requestBody);
  const responseBody = prettyBody(call.responseBody);
  // A four-member enum, not a boolean: `stored-request-only` HAS a request body
  // (api-call-log.capture.ts:154 retains it) and must render it. Exhaustive by
  // construction, so a fifth member fails `tsc` rather than falling through to a
  // note that hides a stored body.
  const bodies: 'both' | 'request-only' | 'none' = ((value): 'both' | 'request-only' | 'none' => {
    switch (value) {
      case 'stored':
        return 'both';
      case 'stored-request-only':
        return 'request-only';
      case 'suppressed-auth-route':
      case 'suppressed-unmatched-anonymous':
        return 'none';
    }
  })(call.bodiesStored);
  // Heading and breadcrumb name the CONCRETE path — that is what an operator
  // came here to see. The template stays visible in the field list below.
  const path = resolvedPath(call.endpoint, call.pathParams);
  const pathParamEntries = Object.entries(call.pathParams ?? {});

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={ClockIcon}
            crumbs={[{ label: t('title'), to: '/logs/api-calls' }, { label: `${call.method} ${path}` }]}
          />
        }
      />
      <ResourceHeader
        icon={ClockIcon}
        title={`${call.method} ${path}`}
        meta={`${call.statusCode} · ${t(`outcomes.${call.outcome}`)}`}
      />
      <div className="space-y-8 px-6 pb-8">
        <section className="space-y-2">
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <dt className="text-text-tertiary">{tDetail('id-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{call.id}</dd>
            <dt className="text-text-tertiary">{tDetail('request-id-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{call.requestId}</dd>
            <dt className="text-text-tertiary">{tDetail('occurred-at-label')}</dt>
            <dd className="text-text-primary">
              {intl.formatDate(call.occurredAt, { dateStyle: 'long', timeStyle: 'medium' })}
            </dd>
            <dt className="text-text-tertiary">{tDetail('credential-class-label')}</dt>
            <dd className="text-text-primary">{t(`credential-classes.${call.credentialClass}`)}</dd>
            {call.rejectionReason !== null && (
              <>
                <dt className="text-text-tertiary">{tDetail('rejection-reason-label')}</dt>
                <dd className="text-text-primary">{t(`rejection-reasons.${call.rejectionReason}`)}</dd>
              </>
            )}
            <dt className="text-text-tertiary">{tDetail('method-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{call.method}</dd>
            <dt className="text-text-tertiary">{tDetail('endpoint-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{call.endpoint}</dd>
            <dt className="text-text-tertiary">{tDetail('path-params-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">
              {pathParamEntries.length === 0 ? (
                placeholder
              ) : (
                <ul>
                  {pathParamEntries.map(([name, value]) => (
                    <li key={name}>
                      <span className="text-text-tertiary">{name}</span> {value}
                    </li>
                  ))}
                </ul>
              )}
            </dd>
            <dt className="text-text-tertiary">{tDetail('mcp-method-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{call.mcpMethod ?? placeholder}</dd>
            <dt className="text-text-tertiary">{tDetail('tool-name-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{call.toolName ?? placeholder}</dd>
            <dt className="text-text-tertiary">{tDetail('status-label')}</dt>
            <dd className="text-text-primary tabular-nums">{call.statusCode}</dd>
            <dt className="text-text-tertiary">{tDetail('outcome-label')}</dt>
            <dd className="text-text-primary">{t(`outcomes.${call.outcome}`)}</dd>
            <dt className="text-text-tertiary">{tDetail('duration-label')}</dt>
            <dd className="text-text-primary tabular-nums">{tDetail('duration-value', { ms: call.durationMs })}</dd>
            <dt className="text-text-tertiary">{tDetail('permission-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{call.permission ?? placeholder}</dd>
          </dl>
        </section>

        <section className="space-y-2">
          <SectionHeading>{tDetail('origin-heading')}</SectionHeading>
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <dt className="text-text-tertiary">{tDetail('workspace-label')}</dt>
            <dd className="text-text-primary">{labelFor(call.workspaceName, call.workspaceId, placeholder)}</dd>
            <dt className="text-text-tertiary">{tDetail('api-key-label')}</dt>
            <dd className="text-text-primary">{labelFor(call.apiKeyName, call.apiKeyId, placeholder)}</dd>
            <dt className="text-text-tertiary">{tDetail('member-label')}</dt>
            <dd className="text-text-primary">{labelFor(call.memberEmail, call.memberId, placeholder)}</dd>
            <dt className="text-text-tertiary">{tDetail('operating-admin-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">
              {labelFor(call.operatingAdminEmail, call.operatingAdminId, placeholder)}
            </dd>
            <dt className="text-text-tertiary">{tDetail('client-ip-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{call.clientIp ?? placeholder}</dd>
            <dt className="text-text-tertiary">{tDetail('user-agent-label')}</dt>
            <dd className="text-text-primary">{call.userAgent ?? placeholder}</dd>
          </dl>
        </section>

        {bodies === 'none' ? (
          <section className="space-y-2">
            <p className="text-text-tertiary">{tDetail(call.bodiesStored)}</p>
          </section>
        ) : (
          <>
            <section className="space-y-2">
              <SectionHeading>{tDetail('request-body-heading')}</SectionHeading>
              {requestBody === null ? (
                <p className="text-text-tertiary">{tDetail('no-body')}</p>
              ) : (
                <pre className="max-h-96 overflow-auto rounded-sm border border-border bg-subtle p-3 font-mono text-sm text-text-primary">
                  {requestBody}
                </pre>
              )}
            </section>

            <section className="space-y-2">
              <SectionHeading>{tDetail('response-body-heading')}</SectionHeading>
              {bodies === 'request-only' ? (
                <p className="text-text-tertiary">{tDetail('stored-request-only')}</p>
              ) : responseBody === null ? (
                <p className="text-text-tertiary">{tDetail('no-body')}</p>
              ) : (
                <pre className="max-h-96 overflow-auto rounded-sm border border-border bg-subtle p-3 font-mono text-sm text-text-primary">
                  {responseBody}
                </pre>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}
